import clsx from 'clsx'
import { CircleCheck, Flag } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { isDuplicateReport, type ReportAgentInput } from '@/api/engagement.ts'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Button, Textarea } from '@/components/ui/primitives.tsx'
import { useReportAgent } from '@/hooks/use-engagement.ts'
import { useI18n } from '@/i18n/index.ts'
import { REPORT_CATEGORIES } from './report-categories.ts'

const NOTE_MAX = 500

/**
 * Reporting an agent (AGM-123).
 *
 * A dialog, focus-trapped like the first-use notice, because reporting is a deliberate act
 * with a consequence for someone else — it should cost a moment's attention and an explicit
 * send, never a stray click on a listing page.
 *
 * A second report on an agent this person already reported is answered with "you already
 * reported this", not an error. The first one DID land; an apology screen would read as
 * failure and invite the reporter to try again, which is precisely the volume the dedupe
 * exists to stop (and volume never suspends anything, by design).
 */
export const ReportDialog = ({
  agentId,
  agentName,
  conversationId,
  onClose
}: {
  agentId: string
  agentName: string
  /** Present when the report starts from a conversation, so an admin can open the flagged chat. */
  conversationId?: string
  onClose: () => void
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const fieldId = useId()
  const report = useReportAgent(agentId)
  // Both read inside the body, never captured at module scope: a captured pack renders the
  // language that was active when this file was first imported (PRD-27 F-3, LOC-012). The
  // ORDER of the categories is not copy and stays in its own module (LOC-017).
  const i18n = useI18n()
  const t = i18n.t.marketplace.report

  const [category, setCategory] = useState<ReportAgentInput['category']>('wrong')
  const [note, setNote] = useState('')
  const [sent, setSent] = useState(false)

  const busy = report.isPending
  useFocusTrap(dialogRef, { onEscape: busy ? undefined : onClose })

  const duplicate = report.isError && isDuplicateReport(report.error)

  const submit = () => {
    report.mutate(
      { category, note: note.trim(), ...(conversationId ? { conversationId } : {}) },
      { onSuccess: () => setSent(true) }
    )
  }

  return (
    <Overlay>
      <div className="fade-in fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)]/45 p-4 backdrop-blur-[2px]">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${fieldId}-title`}
          tabIndex={-1}
          // `shadow-frame` is a utility and therefore beats the base layer's focus ring, which
          // is what keeps the trap's programmatic focus from drawing a ring round the whole
          // dialog the moment it opens.
          className="w-full max-w-md overflow-clip rounded-shell border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <div className="flex items-start gap-3 px-5 pt-5">
            <span
              aria-hidden
              className="mt-0.5 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-tile border border-rule bg-alert-soft text-alert"
            >
              <Flag size={14} strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h2 id={`${fieldId}-title`} className="text-[18px]">
                {t.title(agentName)}
              </h2>
              <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{t.lead}</p>
            </div>
          </div>

          {sent ? (
            <div className="px-5 py-5">
              <p role="status" className="flex items-start gap-2 text-[13.5px] text-grounded">
                <CircleCheck size={14} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
                {t.sent}
              </p>
              <div className="mt-4 flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  {t.close}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4">
                <fieldset>
                  <legend className="eyebrow mb-2">{t.categoryLabel}</legend>
                  <div className="flex flex-col gap-1.5">
                    {REPORT_CATEGORIES.map((value) => (
                      <label
                        key={value}
                        className={clsx(
                          'flex cursor-pointer items-start gap-2.5 rounded-tile border px-3 py-2 text-[13px] leading-snug',
                          'transition-[background-color,border-color] duration-200',
                          category === value ? 'border-rule-strong bg-card' : 'border-rule hover:border-rule-strong'
                        )}
                      >
                        <input
                          type="radio"
                          name={`${fieldId}-category`}
                          value={value}
                          checked={category === value}
                          disabled={busy}
                          onChange={() => setCategory(value)}
                          className="mt-[3px] h-3.5 w-3.5 shrink-0 accent-primary"
                        />
                        <span className="text-pretty">{t.categories[value]}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label htmlFor={`${fieldId}-note`} className="eyebrow mt-4 mb-1.5 block">
                  {t.noteLabel}
                </label>
                <Textarea
                  id={`${fieldId}-note`}
                  rows={3}
                  value={note}
                  maxLength={NOTE_MAX}
                  placeholder={t.notePlaceholder}
                  disabled={busy}
                  onChange={(event) => setNote(event.target.value)}
                />
                <p className="readout mt-1 text-right text-fg-subtle">
                  {t.noteRemaining(i18n, NOTE_MAX - note.length)}
                </p>

                {report.isError && (
                  <p role="alert" className="mt-2 text-[13px] text-alert">
                    {duplicate ? t.duplicate : t.failed}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-rule px-5 py-3.5">
                <Button variant="ghost" onClick={onClose} disabled={busy}>
                  {t.cancel}
                </Button>
                <Button variant="primary" onClick={submit} disabled={busy || duplicate}>
                  {busy ? t.submitting : t.submit}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Overlay>
  )
}
